/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ANALYTICS_PERMISSIONS } from "components/strategyDashboard/permissions";
import { useLocalStorageState } from "./useLocalStorageState";
import { Permission } from "../store/api/generatedApi";
import { useIsAdmin, useTenant, useTenantPermissions } from "./settings";
import {
  PERMISSION_VALUES,
  PERMISSIONS_WITHOUT_TENANT_MEMBERSHIP,
} from "components/users/permissions";
import { useAppSelector } from "src/store/store";
import {
  CRANE_DRIVER_PERMISSIONS,
  CRANE_HEAT_RECIPE_VIEW_PERMISSIONS,
  cranePagesEnabled,
} from "components/crane/permissions";
import { heatOverridesEnabled } from "components/heatQueue/permissions";
import { useTenantDataOptional } from "contexts/tenant";

interface MeticulousWindow extends Window {
  Meticulous?: {
    isRunningAsTest: boolean;
  };
}

export const useAdminPermissions = () => {
  const isTokenAdmin = useAppSelector((state) => state.settings.isAdmin);
  const defaultAdminPermissions = isTokenAdmin
    ? Array.from(PERMISSION_VALUES)
    : [];
  const [adminPermissions, setAdminPermissions] = useLocalStorageState(
    "adminPermissions",
    defaultAdminPermissions
  );
  return {
    adminPermissions,
    togglePermission: (permission: Permission) => {
      setAdminPermissions((prev) => {
        if (prev.includes(permission)) {
          return prev.filter((p) => p !== permission);
        } else {
          return [...prev, permission];
        }
      });
    },
    enableAllPermissions: () => {
      setAdminPermissions(Array.from(PERMISSION_VALUES));
    },
    disableAllPermissions: () => {
      setAdminPermissions([]);
    },
  };
};

export const getIsMeticulous = () => {
  return (window as MeticulousWindow).Meticulous?.isRunningAsTest ?? false;
};

export const usePermission = (permissionsToCheck: Permission[]): boolean => {
  const hasPermission = useHasPermission();
  return permissionsToCheck.some((permission) => hasPermission(permission));
};

export const useHeatOverridesEnabled = (): boolean =>
  heatOverridesEnabled(useTenantDataOptional().heat_override_mode);

export const useCranePagesEnabled = (): boolean =>
  cranePagesEnabled(useTenantDataOptional().crane_driver_view);

export const useCraneDriverViewPermissions = (): Permission[] => {
  const { crane_driver_view: craneDriverView } = useTenantDataOptional();
  return craneDriverView === "heat_recipes"
    ? CRANE_HEAT_RECIPE_VIEW_PERMISSIONS
    : CRANE_DRIVER_PERMISSIONS;
};

export const useHasPermission = (): ((permission: Permission) => boolean) => {
  const tenant = useTenant();
  return useHasTenantPermission(tenant);
};

export const useHasTenantPermission = (
  tenant: string | null
): ((permission: Permission) => boolean) => {
  const tenantPermissions = useTenantPermissions();
  const isAdmin = useIsAdmin();
  const { adminPermissions } = useAdminPermissions();
  const isMeticulous = getIsMeticulous();

  if (tenant == null) {
    return () => false;
  }

  const permissions = tenantPermissions[tenant] ?? [];

  if (isMeticulous) return () => true;

  if (isAdmin) {
    return (permission: Permission) => adminPermissions.includes(permission);
  }

  const hasPermission = (permission: Permission) => {
    return permissions.includes(permission);
  };

  return hasPermission;
};

/** Checks a permission across every tenant without requiring tenant context. */
export const useHasPermissionOnAnyTenant = (
  permission: Permission
): boolean => {
  const tenantPermissions = useTenantPermissions();
  const isAdmin = useIsAdmin();
  const { adminPermissions } = useAdminPermissions();
  if (isAdmin) {
    return adminPermissions.includes(permission);
  }
  return Object.values(tenantPermissions).some((permissions) =>
    permissions.includes(permission)
  );
};

const useMemberTenants = (): string[] =>
  Object.entries(useTenantPermissions())
    .filter(([, permissions]) =>
      permissions.some(
        (permission) =>
          !PERMISSIONS_WITHOUT_TENANT_MEMBERSHIP.includes(permission)
      )
    )
    .map(([tenant]) => tenant)
    .sort();

export const useDefaultTenant = (): {
  defaultTenant: string | null;
} => {
  const isAdmin = useIsAdmin();
  const hasOverviewAccess = useHasOverviewAccessOnAnyTenant();
  const userTenants = useMemberTenants();

  return {
    defaultTenant:
      (userTenants.length === 1 || !hasOverviewAccess) && !isAdmin
        ? userTenants[0]!
        : null,
  };
};

export const useHasAccessToMissionControl = (): boolean => {
  const isAdmin = useIsAdmin();
  const userTenants = useMemberTenants();
  const hasOverviewAccess = useHasOverviewAccessOnAnyTenant();
  return isAdmin || (userTenants.length > 1 && hasOverviewAccess);
};

export const useHasTenant = (tenant: string) => {
  const isAdmin = useIsAdmin();
  const userTenants = useMemberTenants();

  return isAdmin || userTenants.includes(tenant);
};

export const OVERVIEW_PERMISSIONS: Permission[] = [
  "read",
  "publish",
  "crane_supervisor_viewer",
  "manage_overrides_catalog",
  "view_override_history",
  "manage_target_baskets",
  "scenario_planning",
  ...ANALYTICS_PERMISSIONS,
];

const NAVIGATION_PERMISSIONS = OVERVIEW_PERMISSIONS.filter(
  (permission) => permission !== "crane_supervisor_viewer"
);

const useHasPageAccessOnAnyTenant = (
  requiredPermissions: Permission[]
): boolean => {
  const isAdmin = useIsAdmin();
  const tenantPermissions = useTenantPermissions();
  return (
    isAdmin ||
    Object.values(tenantPermissions).some((permissions) =>
      permissions.some((permission) => requiredPermissions.includes(permission))
    )
  );
};

export const useHasOverviewAccessOnAnyTenant = (): boolean =>
  useHasPageAccessOnAnyTenant(OVERVIEW_PERMISSIONS);

export const useHasNavigationAccess = (): boolean =>
  useHasPageAccessOnAnyTenant(NAVIGATION_PERMISSIONS);
